import { useEffect, useRef, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/lib/auth';
import type { ChatMessage } from '@/lib/types';

interface MsgRow extends ChatMessage {
  id: string;
}

export function QAPanel({ accountId }: { accountId: string }) {
  const { user } = useAuth();
  const [convId, setConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MsgRow[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) return;
    const id = `${accountId}-${user.uid}`;
    setConvId(id);
    void setDoc(
      doc(db, 'accounts', accountId, 'conversations', id),
      { ownerUid: user.uid, accountId },
      { merge: true },
    );
  }, [accountId, user]);

  useEffect(() => {
    if (!convId) return;
    const q = query(
      collection(
        db,
        'accounts',
        accountId,
        'conversations',
        convId,
        'messages',
      ),
      orderBy('createdAt', 'asc'),
    );
    const unsub = onSnapshot(q, (snap) => {
      const rows: MsgRow[] = snap.docs.map(
        (d) => ({ id: d.id, ...(d.data() as ChatMessage) }),
      );
      setMessages(rows);
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: 'smooth',
        });
      });
    });
    return unsub;
  }, [convId, accountId]);

  const onSend = async () => {
    const text = input.trim();
    if (!text || !convId) return;
    setInput('');
    setBusy(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'askDealQuestion');
      await fn({ accountId, conversationId: convId, question: text });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card flex h-[640px] flex-col">
      <div className="border-b border-slate-200 px-4 py-3">
        <h2 className="text-sm font-semibold">Ask about this deal</h2>
        <p className="text-xs text-slate-500">
          Answers are grounded in this account's call analyses.
        </p>
      </div>
      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
        {messages.length === 0 && (
          <div className="text-xs text-slate-400">
            Try: "Who is the economic buyer?" or "What's blocking this deal?"
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex ${
              m.role === 'user' ? 'justify-end' : 'justify-start'
            }`}
          >
            <div
              className={`max-w-[85%] whitespace-pre-line rounded-lg px-3 py-2 text-sm ${
                m.role === 'user'
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-800'
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
      </div>
      {error && (
        <div className="border-t border-rose-200 bg-rose-50 px-4 py-2 text-xs text-rose-700">
          {error}
        </div>
      )}
      <div className="border-t border-slate-200 p-3">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void onSend();
              }
            }}
            disabled={busy}
            placeholder="Ask a question…"
            className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          />
          <button
            onClick={onSend}
            disabled={busy || !input.trim()}
            className="btn-primary"
          >
            {busy ? '…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
